import { soldLineSet, type ComponentGroupableLine } from '@/lib/sales/service-components';
import type { EstimateStatus } from '@/types/estimate';

const ACCEPTED_SCOPE_STATUSES = new Set<EstimateStatus>(['APPROVED', 'CONVERTED']);

export function hasAcceptedEstimateScope(status: EstimateStatus): boolean {
  return ACCEPTED_SCOPE_STATUSES.has(status);
}

/**
 * Accepted estimate views and final PDFs are the record of what the customer
 * bought, not a copy of every option they were offered. Keep the complete
 * proposal while it is actionable, then remove declined rows (and every
 * component owned by a declined service) from the accepted presentation.
 */
export function estimateForAcceptedScope<
  TLine extends ComponentGroupableLine & {
    isOptional?: boolean | null;
    isSelected?: boolean | null;
  },
  TEstimate extends { status: EstimateStatus; lineItems: TLine[] },
>(estimate: TEstimate, acceptedScopeOnly = false): TEstimate {
  if (!acceptedScopeOnly && !hasAcceptedEstimateScope(estimate.status)) {
    return estimate;
  }

  const lineItems = estimate.lineItems ?? [];
  const sold = soldLineSet(lineItems);
  if (sold.size === lineItems.length) return estimate;

  return {
    ...estimate,
    lineItems: lineItems.filter((line) => sold.has(line)),
  };
}
